import { useCallback } from 'react'
import { navigate } from 'gatsby'

import { AppStatus, store } from '@/redux'
import { ProviderId } from '@/features/Auth/providers/types'
import { signIn } from '@/features/Auth/session/signIn'
import { authStore } from '@/features/Auth/store/authStore'

/**
 * What a screen needs from signing in: the state, and the actions that move it.
 *
 * Safe to call from more than one place — it reads a shared store and dispatches. The
 * effects that react to a state change live in `useAuthFlow`, which is mounted once, so that
 * two components holding this hook cannot navigate twice for the same transition.
 */
export function useAuth() {
  const state = authStore.use()

  // Named rather than left as a status to inspect. A component asking `state.status ===
  // 'registering'` is a component that knows the machine's shape, and the next state added is
  // a condition it silently stops covering.
  const registration = state.status === 'registering' ? state : null

  const signInWith = useCallback(
    (provider: ProviderId, input?: unknown) => signIn(provider as never, input as never, authStore.dispatch),
    []
  )

  const handleProviderPress = useCallback(
    (provider: ProviderId) => {
      if (provider === 'email') {
        // E-mail is a provider like any other, but it is the one that needs a form before it
        // can produce a credential.
        if (store.getState().AppStatus.modals?.login) {
          AppStatus.transitionModals(['login', 'signup'])
        } else {
          navigate('/auth/signup/')
        }

        return
      }

      // LinkedIn needs its own popup to produce a code before the provider can run, and that
      // popup is opened by a hook. `LinkedInButton` holds it and calls `signInWith` with the
      // access token, so this hook never learns that LinkedIn is different.
      signInWith(provider)
    },
    [signInWith]
  )

  return {
    state,
    registration,
    signInWith,
    handleProviderPress
  }
}
